import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Database, HardDrive, Search, Zap } from 'lucide-react';

// ─── Simulation Engine ────────────────────────────────────────────────────────
// MemTable capacity = 5. After the 5th write the flush is shown as the NEXT
// dedicated step (type:'flush'). This keeps each step atomic and explainable.

const MEMTABLE_CAPACITY = 5;

const RAW_OPS = [
  { type: 'write', key: 'user:1',    value: 'alice' },
  { type: 'write', key: 'user:2',    value: 'bob' },
  { type: 'write', key: 'score:1',   value: '42' },
  { type: 'write', key: 'user:3',    value: 'carol' },
  { type: 'write', key: 'score:2',   value: '88' },
  // ↑ 5 entries → flush automatically emitted after this
  { type: 'write', key: 'user:1',    value: 'alice_v2' },  // duplicate post-flush
  { type: 'write', key: 'config:db', value: 'postgres' },
  { type: 'read',  key: 'user:1' },                        // memtable hit (freshest)
  { type: 'read',  key: 'ghost:key' },                     // bloom filter miss
  { type: 'write', key: 'score:3',   value: '15' },
  { type: 'write', key: 'user:4',    value: 'dave' },
  { type: 'write', key: 'score:4',   value: '77' },
  // ↑ 5 entries → flush automatically emitted
  { type: 'read',  key: 'score:1' },                       // SSTable read path
  { type: 'write', key: 'user:2',    value: 'bob_v2' },    // duplicate across levels
  { type: 'write', key: 'score:1',   value: '99' },        // duplicate across levels
  { type: 'read',  key: 'user:2' },                        // memtable wins
  { type: 'compact' },
  { type: 'read',  key: 'score:1' },                       // post-compaction
];

function buildSteps() {
  const steps = [];
  let memtable = [];
  let sstables = [];
  let ts = 0;
  let ssId = 1;

  // Inject auto-flush after every MEMTABLE_CAPACITY writes
  const ops = [];
  let writeCount = 0;
  for (const op of RAW_OPS) {
    ops.push(op);
    if (op.type === 'write') {
      writeCount++;
      if (writeCount % MEMTABLE_CAPACITY === 0) {
        ops.push({ type: 'flush' });
      }
    }
  }

  for (const op of ops) {
    ts++;

    // ── WRITE ──────────────────────────────────────────────────────────────
    if (op.type === 'write') {
      const dupInMem  = memtable.some(e => e.key === op.key);
      const dupInSst  = sstables.some(s => s.entries.some(e => e.key === op.key));
      memtable = [...memtable, { key: op.key, value: op.value, ts }];

      let edgeCase = null;
      let explanation = '';
      let tip = null;

      if (dupInMem) {
        edgeCase = 'dup-mem';
        explanation =
          `"${op.key}" already exists in the MemTable. LSM Trees don't overwrite — ` +
          `they append a new version with a higher timestamp. Both entries live in the ` +
          `MemTable temporarily. On reads, the highest timestamp wins. ` +
          `Older copies are garbage-collected during compaction.`;
      } else if (dupInSst) {
        edgeCase = 'dup-sst';
        explanation =
          `"${op.key}" exists in a flushed SSTable. The new write goes to the MemTable as usual. ` +
          `Because the MemTable is always checked first on reads, this newer value will shadow ` +
          `the old one immediately. Stale SSTable copies get cleaned up at the next compaction.`;
      } else {
        const capacity = memtable.length;
        explanation =
          `Write "${op.key}" → "${op.value}" appended to the MemTable. ` +
          `The MemTable is an in-memory sorted tree (Red-Black / Skip List), so each insert ` +
          `is O(log n). Capacity: ${capacity}/${MEMTABLE_CAPACITY}.`;
        tip = 'logn';
      }

      steps.push({
        id: steps.length,
        phase: 'write',
        op,
        memtable: [...memtable],
        sstables: sstables.map(s => ({ ...s })),
        explanation,
        edgeCase,
        tip,
        highlightKey: op.key,
        title: `Write  ${op.key} = "${op.value}"`,
        badge: dupInMem ? 'Duplicate in MemTable' : dupInSst ? 'Duplicate across levels' : null,
      });

    // ── READ ──────────────────────────────────────────────────────────────
    } else if (op.type === 'read') {
      const memHit = [...memtable].reverse().find(e => e.key === op.key) || null;
      let sstHit = null;
      const searchPath = [];

      if (!memHit) {
        for (let i = sstables.length - 1; i >= 0; i--) {
          const s = sstables[i];
          const inBloom = s.bloomFilter.has(op.key);
          if (!inBloom) {
            searchPath.push({ sstId: s.id, result: 'bloom-miss' });
          } else {
            const found = s.entries.find(e => e.key === op.key);
            searchPath.push({ sstId: s.id, result: found ? 'found' : 'not-found' });
            if (found) { sstHit = { ...found, sstId: s.id }; break; }
          }
        }
      }

      let explanation = '', edgeCase = null;

      if (memHit) {
        explanation =
          `Read "${op.key}": MemTable check → HIT (value: "${memHit.value}", ts=${memHit.ts}). ` +
          `MemTable is always the first stop — it holds the freshest writes. Zero disk I/O.`;
      } else if (searchPath.every(p => p.result === 'bloom-miss') && !sstHit) {
        edgeCase = 'bloom-miss';
        explanation =
          `Read "${op.key}": Not in MemTable. Bloom filter checked for every SSTable — ` +
          `all say "definitely not here". No disk read needed at all! ` +
          `Bloom filters can have false positives (say "maybe" when the key is absent) ` +
          `but never false negatives. Key does not exist → returns null.`;
      } else if (sstHit) {
        const path = searchPath.map(p =>
          `SST-${p.sstId} [${p.result === 'bloom-miss' ? 'bloom ✗' : p.result === 'found' ? 'found ✓' : 'bloom ✓, not found'}]`
        ).join(' → ');
        explanation =
          `Read "${op.key}": MemTable miss. Read path: ${path}. ` +
          `Sparse index on SST-${sstHit.sstId} narrowed the file range, found "${sstHit.value}" (ts=${sstHit.ts}).`;
      } else {
        edgeCase = 'not-found';
        explanation =
          `Read "${op.key}": Not in MemTable, not in any SSTable (bloom filters all said no). ` +
          `Returns null — with zero wasted disk I/O.`;
      }

      steps.push({
        id: steps.length,
        phase: 'read',
        op,
        memtable: [...memtable],
        sstables: sstables.map(s => ({ ...s })),
        explanation,
        edgeCase,
        tip: null,
        highlightKey: op.key,
        title: `Read  ${op.key}`,
        badge: edgeCase === 'bloom-miss' ? 'Bloom Filter Miss' : edgeCase === 'not-found' ? 'Key Not Found' : null,
        readResult: memHit || sstHit || null,
        searchPath,
        memHit: !!memHit,
        sstHit,
      });

    // ── FLUSH ─────────────────────────────────────────────────────────────
    } else if (op.type === 'flush') {
      const sorted = [...memtable].sort((a, b) => a.key < b.key ? -1 : 1);
      const bloom = new Set(sorted.map(e => e.key));
      // sparse index: pointer every 2 keys
      const sparse = sorted
        .filter((_, i) => i % 2 === 0)
        .map((e, i) => ({ key: e.key, offset: i * 2 }));
      const sst = { id: ssId++, entries: sorted, bloomFilter: bloom, sparseIndex: sparse };
      sstables = [...sstables, sst];
      const prevMem = [...memtable];
      memtable = [];

      steps.push({
        id: steps.length,
        phase: 'flush',
        op,
        memtable: [],
        sstables: sstables.map(s => ({ ...s })),
        explanation:
          `MemTable reached capacity (${MEMTABLE_CAPACITY}/${MEMTABLE_CAPACITY}). ` +
          `All ${prevMem.length} entries are sorted by key and written to SSTable-${sst.id} on disk. ` +
          `Simultaneously, a Bloom Filter is built (all ${bloom.size} keys hashed into a bit array) ` +
          `and a Sparse Index is created (one pointer per 2 keys = ${sparse.length} entries). ` +
          `MemTable is cleared. Writes can resume immediately.`,
        edgeCase: null,
        tip: null,
        highlightKey: null,
        title: `Flush → SSTable-${sst.id}`,
        badge: null,
        flushedEntries: prevMem,
        flushedSst: sst,
      });

    // ── COMPACT ───────────────────────────────────────────────────────────
    } else if (op.type === 'compact') {
      const allEntries = sstables.flatMap(s => s.entries);
      const latest = {};
      for (const e of allEntries) {
        if (!latest[e.key] || e.ts > latest[e.key].ts) latest[e.key] = e;
      }
      const merged = Object.values(latest).sort((a, b) => a.key < b.key ? -1 : 1);
      const bloom = new Set(merged.map(e => e.key));
      const sparse = merged.filter((_, i) => i % 2 === 0).map((e, i) => ({ key: e.key, offset: i * 2 }));
      const compacted = { id: ssId++, entries: merged, bloomFilter: bloom, sparseIndex: sparse, isCompacted: true };
      const oldIds = sstables.map(s => s.id);
      const removedCount = allEntries.length - merged.length;
      sstables = [compacted];

      steps.push({
        id: steps.length,
        phase: 'compact',
        op,
        memtable: [...memtable],
        sstables: [compacted],
        explanation:
          `Compaction: SSTable-${oldIds.join(' + SSTable-')} are merged into SSTable-${compacted.id}. ` +
          `Duplicate keys resolved — only the highest-timestamp value per key survives. ` +
          `${removedCount} stale entr${removedCount === 1 ? 'y' : 'ies'} removed. ` +
          `Result: ${merged.length} unique keys. New bloom filter and sparse index rebuilt. ` +
          `Old SSTables deleted from disk.`,
        edgeCase: 'compact',
        tip: null,
        highlightKey: null,
        title: `Compaction → SSTable-${compacted.id}`,
        badge: 'Compaction',
        oldSstIds: oldIds,
        newSstId: compacted.id,
      });
    }
  }

  return steps;
}

const STEPS = buildSteps();

// ─── Colours ──────────────────────────────────────────────────────────────────

const PHASE = {
  write:   { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', bg: 'bg-emerald-50 dark:bg-emerald-950/40', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300' },
  read:    { dot: 'bg-blue-500',    text: 'text-blue-600 dark:text-blue-400',       border: 'border-blue-200 dark:border-blue-800',       bg: 'bg-blue-50 dark:bg-blue-950/40',       badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300' },
  flush:   { dot: 'bg-violet-500',  text: 'text-violet-600 dark:text-violet-400',   border: 'border-violet-200 dark:border-violet-800',   bg: 'bg-violet-50 dark:bg-violet-950/40',   badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300' },
  compact: { dot: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',     border: 'border-amber-200 dark:border-amber-800',     bg: 'bg-amber-50 dark:bg-amber-950/40',     badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300' },
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────

function LogNTooltip({ children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-block">
      <span
        className="underline decoration-dotted decoration-blue-500 cursor-help text-blue-600 dark:text-blue-400 font-semibold"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen(o => !o)}
      >
        {children}
      </span>
      {open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-72 p-3 rounded-xl shadow-xl
          bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-left
          text-xs text-gray-700 dark:text-gray-300 leading-relaxed pointer-events-none">
          <span className="block font-bold text-gray-900 dark:text-white mb-1">Why O(log n), not O(1)?</span>
          The MemTable uses a sorted structure — a <strong>Red-Black Tree</strong> or <strong>Skip List</strong> — not a HashMap.
          <span className="block mt-1">Sorted order is mandatory because SSTables must be written in key-sorted order on flush. A HashMap gives O(1) inserts but keys are unordered, so sorting at flush would cost O(n log n) per flush.</span>
          <span className="block mt-1">The sorted tree keeps keys in order at all times → flush is a cheap O(n) sequential scan.</span>
        </span>
      )}
    </span>
  );
}

// ─── SQL Table ────────────────────────────────────────────────────────────────

function SqlTable({ rows, highlightKey, dimmed = false, showRowNum = true }) {
  if (rows.length === 0) {
    return (
      <p className="text-xs text-gray-400 dark:text-gray-600 italic py-2 px-3">— empty —</p>
    );
  }
  return (
    <div className={`overflow-x-auto transition-opacity duration-300 ${dimmed ? 'opacity-40' : ''}`}>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            {showRowNum && <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500 w-8">#</th>}
            <th className="text-left py-1.5 px-3 font-semibold text-gray-500 dark:text-gray-400">key</th>
            <th className="text-left py-1.5 px-3 font-semibold text-gray-500 dark:text-gray-400">value</th>
            <th className="text-left py-1.5 px-3 font-semibold text-gray-500 dark:text-gray-400">ts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isHl = highlightKey && row.key === highlightKey;
            return (
              <tr
                key={`${row.key}-${row.ts}`}
                className={`border-b border-gray-100 dark:border-gray-800 transition-colors
                  ${isHl ? 'bg-blue-50 dark:bg-blue-950/50' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}
              >
                {showRowNum && (
                  <td className="py-1.5 px-3 text-gray-300 dark:text-gray-600 tabular-nums">{i + 1}</td>
                )}
                <td className={`py-1.5 px-3 font-mono font-medium ${isHl ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                  {row.key}
                </td>
                <td className={`py-1.5 px-3 font-mono ${isHl ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {row.value}
                </td>
                <td className="py-1.5 px-3 text-gray-400 dark:text-gray-600 tabular-nums font-mono">t{row.ts}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── MemTable Panel ───────────────────────────────────────────────────────────

function MemTablePanel({ step }) {
  const entries = step.memtable;
  const cap = entries.length;
  const pct = (cap / MEMTABLE_CAPACITY) * 100;
  const barColor = cap === MEMTABLE_CAPACITY ? 'bg-red-500' : cap >= 3 ? 'bg-amber-400' : 'bg-emerald-500';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-2">
          <Database className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">MemTable</span>
          <span className="text-xs text-gray-400">(in-memory · unsorted)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 tabular-nums">{cap} / {MEMTABLE_CAPACITY}</span>
          <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* Insertion complexity note */}
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-blue-50/40 dark:bg-blue-950/20">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Each insert: <LogNTooltip>O(log n)</LogNTooltip>
          <span className="ml-1">— sorted tree keeps keys ordered for flush</span>
        </p>
      </div>

      <SqlTable rows={entries} highlightKey={step.phase === 'write' || step.phase === 'read' ? step.highlightKey : null} />
    </div>
  );
}

// ─── Flush Visualizer ─────────────────────────────────────────────────────────

function FlushVisualizer({ step }) {
  const { flushedEntries, flushedSst } = step;
  if (!flushedEntries || !flushedSst) return null;

  const sorted = [...flushedEntries].sort((a, b) => a.key < b.key ? -1 : 1);

  return (
    <div className="rounded-xl border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-950/20 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-violet-200 dark:border-violet-900 bg-violet-100/60 dark:bg-violet-900/30">
        <Zap className="w-3.5 h-3.5 text-violet-500" />
        <span className="text-xs font-bold text-violet-700 dark:text-violet-300 uppercase tracking-wide">Flush Process</span>
      </div>

      <div className="p-4 space-y-4">
        {/* Step 1: Sort */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-violet-200 dark:bg-violet-800 text-violet-700 dark:text-violet-300 text-[10px] flex items-center justify-center font-bold">1</span>
            Sort by key
          </p>
          <div className="grid grid-cols-2 gap-3">
            {/* Before sort */}
            <div>
              <p className="text-[10px] text-gray-400 mb-1 uppercase tracking-wide">Before (insertion order)</p>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
                <SqlTable rows={flushedEntries} showRowNum={false} />
              </div>
            </div>
            {/* After sort */}
            <div>
              <p className="text-[10px] text-gray-400 mb-1 uppercase tracking-wide">After (sorted by key)</p>
              <div className="rounded-lg border border-violet-300 dark:border-violet-700 overflow-hidden bg-white dark:bg-gray-900">
                <SqlTable rows={sorted} showRowNum={false} />
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Sparse Index */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-violet-200 dark:bg-violet-800 text-violet-700 dark:text-violet-300 text-[10px] flex items-center justify-center font-bold">2</span>
            Build Sparse Index <span className="font-normal text-gray-400">(one pointer per 2 keys)</span>
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-500 dark:text-gray-400">key</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-500 dark:text-gray-400">file offset</th>
                  <th className="text-left py-1.5 px-3 font-semibold text-gray-500 dark:text-gray-400">why</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((e, i) => {
                  const isIndexed = i % 2 === 0;
                  return (
                    <tr key={e.key} className={`border-b border-gray-100 dark:border-gray-800 ${isIndexed ? '' : 'opacity-40'}`}>
                      <td className={`py-1.5 px-3 font-mono ${isIndexed ? 'text-violet-700 dark:text-violet-300 font-medium' : 'text-gray-500'}`}>{e.key}</td>
                      <td className={`py-1.5 px-3 font-mono ${isIndexed ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>{isIndexed ? i : '—'}</td>
                      <td className="py-1.5 px-3 text-gray-400">{isIndexed ? '← indexed' : 'skipped'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Step 3: Bloom Filter */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-violet-200 dark:bg-violet-800 text-violet-700 dark:text-violet-300 text-[10px] flex items-center justify-center font-bold">3</span>
            Build Bloom Filter <span className="font-normal text-gray-400">(hash each key → set bits)</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sorted.map(e => (
              <span key={e.key} className="px-2 py-1 rounded-full border border-violet-300 dark:border-violet-700 bg-white dark:bg-gray-900 text-xs font-mono text-violet-700 dark:text-violet-300">
                {e.key}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5">
            Any key <strong>not</strong> in this set → guaranteed miss (no disk read). False positives possible, false negatives impossible.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Read Flow Visualizer ─────────────────────────────────────────────────────

function ReadFlowVisual({ step }) {
  if (step.phase !== 'read') return null;
  const { memHit, searchPath = [], readResult, sstHit } = step;

  return (
    <div className="rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/20 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-blue-200 dark:border-blue-900 bg-blue-100/60 dark:bg-blue-900/30">
        <Search className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Read Path</span>
        <span className="text-xs text-blue-500 dark:text-blue-400">for key: <span className="font-mono">{step.highlightKey}</span></span>
      </div>

      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* MemTable check */}
          <ReadPathNode
            label="MemTable"
            sublabel="check first"
            result={memHit ? 'hit' : 'miss'}
          />

          {!memHit && searchPath.map((sp, i) => (
            <React.Fragment key={i}>
              <span className="text-gray-400 text-sm">→</span>
              <ReadPathNode
                label={`SST-${sp.sstId}`}
                sublabel={sp.result === 'bloom-miss' ? 'bloom filter ✗' : sp.result === 'found' ? 'sparse index → found' : 'bloom ✓, not found'}
                result={sp.result === 'found' ? 'hit' : sp.result === 'bloom-miss' ? 'bloom-miss' : 'miss'}
              />
            </React.Fragment>
          ))}

          {!memHit && searchPath.length === 0 && (
            <>
              <span className="text-gray-400 text-sm">→</span>
              <ReadPathNode label="No SSTables" sublabel="nothing to check" result="miss" />
            </>
          )}
        </div>

        {/* Result */}
        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Result:</span>
          {readResult ? (
            <span className="px-2.5 py-1 rounded-full text-xs font-mono font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
              "{readResult.value}" (ts={readResult.ts})
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded-full text-xs font-mono font-medium bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
              null — key not found
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ReadPathNode({ label, sublabel, result }) {
  const styles = {
    hit: 'bg-emerald-100 border-emerald-300 dark:bg-emerald-900/40 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300',
    miss: 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600 text-gray-500 dark:text-gray-400',
    'bloom-miss': 'bg-rose-100 border-rose-300 dark:bg-rose-900/30 dark:border-rose-700 text-rose-600 dark:text-rose-400',
  };
  return (
    <div className={`flex flex-col items-center px-3 py-2 rounded-lg border text-xs min-w-[80px] ${styles[result]}`}>
      <span className="font-semibold font-mono">{label}</span>
      <span className="text-[10px] mt-0.5 opacity-75">{sublabel}</span>
    </div>
  );
}

// ─── SSTable Panel ────────────────────────────────────────────────────────────

function SSTPanel({ sst, highlightKey, readSstId }) {
  const isHit = readSstId === sst.id;
  return (
    <div className={`rounded-xl border overflow-hidden transition-all duration-300
      ${sst.isCompacted
        ? 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20'
        : isHit
        ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/20 ring-1 ring-blue-200 dark:ring-blue-800'
        : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
      }`}>
      <div className={`flex items-center gap-2 px-4 py-2.5 border-b text-xs font-bold uppercase tracking-wide
        ${sst.isCompacted
          ? 'border-amber-200 dark:border-amber-900 bg-amber-100/60 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
          : isHit
          ? 'border-blue-200 dark:border-blue-800 bg-blue-100/60 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
          : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400'
        }`}>
        <HardDrive className="w-3.5 h-3.5" />
        SSTable-{sst.id}
        {sst.isCompacted && <span className="ml-1 text-[10px] font-normal opacity-75">(compacted)</span>}
        {isHit && <span className="ml-auto text-[10px] font-normal text-blue-500">← read hit</span>}
      </div>

      <SqlTable rows={sst.entries} highlightKey={isHit ? highlightKey : null} showRowNum={false} />

      <div className="border-t border-gray-100 dark:border-gray-800">
        <details>
          <summary className="px-4 py-2 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer select-none flex items-center gap-1">
            Sparse Index ({sst.sparseIndex.length} pointers)
          </summary>
          <div className="px-4 pb-3">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-1 font-semibold text-gray-400">key</th>
                  <th className="text-left py-1 font-semibold text-gray-400">offset</th>
                </tr>
              </thead>
              <tbody>
                {sst.sparseIndex.map((idx, i) => (
                  <tr key={i} className="border-b border-gray-50 dark:border-gray-800/50">
                    <td className="py-1 font-mono text-gray-500 dark:text-gray-400">{idx.key}</td>
                    <td className="py-1 font-mono text-gray-400">{idx.offset}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>

        <details>
          <summary className="px-4 py-2 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer select-none border-t border-gray-100 dark:border-gray-800">
            Bloom Filter ({sst.bloomFilter.size} keys)
          </summary>
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {[...sst.bloomFilter].map((k, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-[10px] font-mono text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                {k}
              </span>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

// ─── Explanation Card ─────────────────────────────────────────────────────────

function ExplanationCard({ step }) {
  const c = PHASE[step.phase];
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${c.dot}`} />
        <span className={`text-sm font-bold ${c.text}`}>{step.title}</span>
        {step.badge && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>
            {step.badge}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed ml-4">
        {step.explanation}
      </p>
    </div>
  );
}

// ─── Step Sidebar ─────────────────────────────────────────────────────────────

function StepSidebar({ steps, currentIdx, onJump }) {
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentIdx]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 py-2 flex-shrink-0">
        Operations
      </p>
      <div className="flex-1 overflow-y-auto space-y-0.5 px-2 pb-2" style={{ scrollbarWidth: 'thin' }}>
        {steps.map((s, i) => {
          const c = PHASE[s.phase];
          const isCurrent = i === currentIdx;
          const isPast = i < currentIdx;
          return (
            <button
              key={i}
              ref={isCurrent ? activeRef : null}
              onClick={() => onJump(i)}
              className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-[11px] transition-all duration-150 group
                ${isCurrent
                  ? `${c.bg} border ${c.border} font-semibold`
                  : isPast
                  ? 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-300 dark:text-gray-600'
                }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCurrent ? c.dot : isPast ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              <span className={`font-mono leading-tight ${isCurrent ? c.text : ''}`}>
                <span className="text-gray-300 dark:text-gray-600 mr-1">{String(i + 1).padStart(2, '0')}.</span>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LSMTree() {
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = STEPS.length;
  const step = STEPS[idx];

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(total - 1, i));
    setIdx(clamped);
    setAnimKey(k => k + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setIdx(i => Math.min(i + 1, total - 1));
        setAnimKey(k => k + 1);
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setIdx(i => Math.max(i - 1, 0));
        setAnimKey(k => k + 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total]);

  const readSstId = step.phase === 'read' && step.sstHit ? step.sstHit.sstId : null;

  return (
    // Full viewport, no page scroll — layout handles internal scrolling
    <div className="h-screen flex flex-col bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden">

      {/* ── Top bar ── */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/dev" className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dev</span>
            </Link>
            <span className="text-gray-300 dark:text-gray-700">/</span>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">LSM Tree — Interactive Simulator</h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Progress */}
            <span className="text-xs text-gray-400 tabular-nums">{idx + 1} / {total}</span>
            <div className="hidden sm:flex w-28 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-blue-500 to-violet-500 transition-all duration-500"
                style={{ width: `${((idx + 1) / total) * 100}%` }}
              />
            </div>
            <span className="hidden sm:inline text-xs text-gray-400">← → keys</span>
          </div>
        </div>
      </div>

      {/* ── Body (sidebar + content) ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Sidebar */}
        <div className="hidden lg:flex flex-col w-52 xl:w-60 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 overflow-hidden">
          <StepSidebar steps={STEPS} currentIdx={idx} onJump={goTo} />
        </div>

        {/* Main scroll area */}
        <div className="flex-1 overflow-y-auto">
          <div
            key={animKey}
            className="max-w-4xl mx-auto px-4 py-5 space-y-5"
            style={{ animation: 'fadeSlideIn 0.25s ease' }}
          >
            {/* Explanation */}
            <ExplanationCard step={step} />

            {/* Flush visualizer */}
            {step.phase === 'flush' && <FlushVisualizer step={step} />}

            {/* Read flow */}
            {step.phase === 'read' && <ReadFlowVisual step={step} />}

            {/* MemTable */}
            <MemTablePanel step={step} />

            {/* SSTables */}
            {step.sstables.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                  <HardDrive className="w-3.5 h-3.5" />
                  SSTables (on-disk · sorted · immutable)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {step.sstables.map(sst => (
                    <SSTPanel
                      key={sst.id}
                      sst={sst}
                      highlightKey={step.highlightKey}
                      readSstId={readSstId}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Spacer so last card isn't hidden behind the nav bar */}
            <div className="h-4" />
          </div>
        </div>
      </div>

      {/* ── Fixed bottom nav ── */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => goTo(idx - 1)}
            disabled={idx === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>

          {/* Mobile progress */}
          <div className="flex lg:hidden items-center gap-2 text-xs text-gray-400">
            <div className="w-32 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-blue-500 to-violet-500 transition-all duration-500"
                style={{ width: `${((idx + 1) / total) * 100}%` }}
              />
            </div>
            <span className="tabular-nums">{idx + 1}/{total}</span>
          </div>

          <div className="hidden lg:flex items-center gap-2 text-xs text-gray-400">
            {step.badge && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${PHASE[step.phase].badge}`}>
                {step.badge}
              </span>
            )}
            <span className="font-mono">{step.title}</span>
          </div>

          <button
            onClick={() => goTo(idx + 1)}
            disabled={idx === total - 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-transparent bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
